import { h } from 'vue'
import { toast } from 'vue-sonner'

/**
 * Registry of currently-visible toasts keyed by their group key, so repeat
 * calls with the same key collapse into a single stacked toast whose
 * description becomes a bulleted list of every entry.
 *
 * Default grouping is by `${type}::${message}`. Pass an explicit `groupKey`
 * to `pushToast` to differentiate implicitly (e.g. per record id) without
 * changing the visible title.
 */
const activeGroups = new Map()

function resolveGroupKey({ groupKey, type, message }) {
  if (groupKey) return `custom::${groupKey}`
  return `${type}::${message ?? ''}`
}

function renderGroupedDescription(descriptions) {
  if (!descriptions.length) return undefined
  if (descriptions.length === 1) return descriptions[0]
  return () =>
    h(
      'ul',
      { class: 'toast-group-list' },
      descriptions.map((entry, index) =>
        h('li', { key: index, class: 'toast-group-item' }, entry),
      ),
    )
}

function renderTitle(message, count) {
  return count > 1 ? `${message} (${count})` : message
}

function callSonner(type, message, opts) {
  if (type === 'success') return toast.success(message, opts)
  if (type === 'error') return toast.error(message, opts)
  if (type === 'warning') return toast.warning(message, opts)
  return toast(message, opts)
}

/**
 * Push a stackable toast into the bottom-right global container.
 *
 * Repeat calls with the same grouping key combine into one toast: the title
 * stays put, gets a `(n)` count suffix, and each new `description` is
 * appended as a bullet under it. By default the grouping key is
 * `type + message`; pass `groupKey` to override with an implicit
 * differentiator (e.g. record id) not visible in the toast.
 *
 * Options:
 *   type        — 'info' | 'success' | 'error' | 'warning' (default 'info')
 *   message     — headline text
 *   description — optional secondary text; multiple entries render as a list
 *   duration    — auto-dismiss after N ms; pass Infinity to require manual dismissal
 *   action      — { label, onClick } to render a right-side action button
 *   groupKey    — optional stable string; when set, replaces the default
 *                 `type::message` grouping key
 */
export function pushToast({
  type = 'info',
  message,
  description,
  duration = 5000,
  action,
  groupKey,
} = {}) {
  const key = resolveGroupKey({ groupKey, type, message })
  const existing = activeGroups.get(key)

  if (existing) {
    if (description) existing.descriptions.push(description)
    const opts = {
      id: existing.id,
      description: renderGroupedDescription(existing.descriptions),
      duration,
    }
    if (action) opts.action = action
    callSonner(type, renderTitle(message, existing.descriptions.length), opts)
    return existing.id
  }

  const descriptions = description ? [description] : []
  const cleanup = () => activeGroups.delete(key)
  const opts = {
    description: renderGroupedDescription(descriptions),
    duration,
    onDismiss: cleanup,
    onAutoClose: cleanup,
  }
  if (action) opts.action = action

  const id = callSonner(type, renderTitle(message, descriptions.length), opts)
  activeGroups.set(key, { id, descriptions })
  return id
}

/**
 * Push a toast that does NOT auto-dismiss. The user must close it with the
 * X button (Toaster is configured with close-button in BaseLayout.vue).
 * Persists across SPA route changes because the Toaster lives in the shared
 * layout — only a full page reload clears the queue.
 */
export function pushStickyToast({
  type = 'error',
  message,
  description,
  action,
  groupKey,
} = {}) {
  return pushToast({
    type,
    message,
    description,
    duration: Infinity,
    action,
    groupKey,
  })
}

export function dismissToast(id) {
  toast.dismiss(id)
}
